import React from "react";
import {
  Card,
  Checkbox,
  Spinner,
  Subtitle1,
  Text,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";

type RiskCategory = {
  id: number;
  category: string;
  order?: number | null;
  isObjekt?: boolean;
  insuranceClassIds: number[];
};

type InsuranceClass = {
  id: number;
  insuranceClass: string;
  sortOrder?: number | null;
  active?: boolean | null;
};

/**
 * Risiko-Zuordnung (SUH-Cleanup SP2): pflegt die konfigurierbare Zuordnung
 * „welche Sparte kann welches Risikoobjekt vorschlagen" (Tabelle
 * RiskObjectCategoryInsuranceClass). Nur echte Objekt-Kategorien (isObjekt=true)
 * sind Spalten; Zeilen = Sparten (InsuranceClass). Ein Häkchen = diese Sparte
 * schlägt bei der Extraktion diese Objekt-Kategorie vor. Gespeichert wird per
 * Kategorie (PUT /risk-object-categories/:id/insurance-classes).
 */
export const RiskObjectMappingSection: React.FC = () => {
  const [categories, setCategories] = React.useState<RiskCategory[]>([]);
  const [classes, setClasses] = React.useState<InsuranceClass[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState<string | null>(null);
  const toasterId = useId("risk-mapping-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, clsRes] = await Promise.all([
        fetch(apiUrl("risk-object-categories/with-insurance-classes")),
        fetch(apiUrl("insurance-class")),
      ]);
      const cats = catRes.ok ? await catRes.json() : [];
      const cls = clsRes.ok ? await clsRes.json() : [];
      setCategories(
        (Array.isArray(cats) ? cats : []).map((c: RiskCategory) => ({
          ...c,
          insuranceClassIds: Array.isArray(c.insuranceClassIds) ? c.insuranceClassIds : [],
        })),
      );
      setClasses(
        (Array.isArray(cls) ? cls : []).filter((c: InsuranceClass) => c.active !== false),
      );
    } catch {
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler beim Laden</ToastTitle>
          <ToastBody>Die Risiko-Zuordnung konnte nicht geladen werden.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
    setLoading(false);
  }, [dispatchToast]);

  React.useEffect(() => {
    load();
  }, [load]);

  const objectCategories = React.useMemo(
    () =>
      categories
        .filter((c) => c.isObjekt)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.category.localeCompare(b.category, "de")),
    [categories],
  );

  const sortedClasses = React.useMemo(
    () =>
      [...classes].sort(
        (a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          a.insuranceClass.localeCompare(b.insuranceClass, "de"),
      ),
    [classes],
  );

  const toggle = async (cat: RiskCategory, classId: number, checked: boolean) => {
    const before = cat.insuranceClassIds ?? [];
    const set = new Set(before);
    if (checked) set.add(classId);
    else set.delete(classId);
    const next = Array.from(set);

    // optimistisch
    setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, insuranceClassIds: next } : c)));
    const key = `${cat.id}:${classId}`;
    setSavingKey(key);
    try {
      const res = await fetch(apiUrl(`risk-object-categories/${cat.id}/insurance-classes`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insuranceClassIds: next }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
    } catch {
      // zurückrollen
      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, insuranceClassIds: before } : c)));
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler beim Speichern</ToastTitle>
          <ToastBody>Die Zuordnung konnte nicht gespeichert werden.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
    setSavingKey(null);
  };

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <Toaster toasterId={toasterId} position="top-end" />
      <Subtitle1>Risiko-Zuordnung (Vertrag → mögliches Risikoobjekt)</Subtitle1>
      <Text style={{ color: "#666" }}>
        Lege fest, welche Sparte welche Objekt-Kategorie als Risikoobjekt vorschlägt. Nur bei
        gesetzter Zuordnung lässt sich aus einem Vertrag dieser Sparte ein Risikoobjekt der
        jeweiligen Kategorie extrahieren. Deckungen ohne Zuordnung (z. B. Haftpflicht,
        Rechtsschutz) bleiben reine Verträge.
      </Text>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <Spinner size="small" />
        </div>
      ) : objectCategories.length === 0 ? (
        <Text style={{ color: "#666" }}>Keine Objekt-Kategorien vorhanden.</Text>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    borderBottom: "2px solid #ececec",
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                  }}
                >
                  Sparte
                </th>
                {objectCategories.map((cat) => (
                  <th
                    key={cat.id}
                    style={{
                      padding: "8px 12px",
                      borderBottom: "2px solid #ececec",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cat.category}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedClasses.map((cls, idx) => (
                <tr key={cls.id} style={{ background: idx % 2 === 1 ? "#faf9fa" : "#fff" }}>
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #f1f1f1",
                      position: "sticky",
                      left: 0,
                      background: idx % 2 === 1 ? "#faf9fa" : "#fff",
                    }}
                  >
                    {cls.insuranceClass}
                  </td>
                  {objectCategories.map((cat) => {
                    const checked = (cat.insuranceClassIds ?? []).includes(cls.id);
                    const key = `${cat.id}:${cls.id}`;
                    return (
                      <td
                        key={cat.id}
                        style={{
                          padding: "6px 12px",
                          borderBottom: "1px solid #f1f1f1",
                          textAlign: "center",
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={savingKey === key}
                          onChange={(_, data) => toggle(cat, cls.id, Boolean(data.checked))}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default RiskObjectMappingSection;
